/**
 * Impact Analysis Component
 *
 * Shows the "blast radius" of a service - what services would be affected
 * if this service went down, including transitive dependencies
 */

import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import type { Service } from '../../graphql/types';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import '@tylertech/forge/icon';
import './ImpactAnalysis.css';

/**
 * Dependency with nested service data from GraphQL
 */
interface GraphQLDependency {
  fromServiceId: string;
  toServiceId: string;
  metadata: {
    dependencyType: string;
    communicationPattern: string;
    criticality?: string | null;
    notes?: string | null;
  };
  toService?: Service | null;
  fromService?: Service | null;
}

/**
 * Analyzed impact level for a service
 */
interface ImpactLevel {
  service: Service;
  level: number; // 0 = direct dependent, 1+ = transitive
  path: string[]; // Service IDs showing dependency chain
  dependencyType: 'hard' | 'soft';
  criticality?: string | null;
}

/**
 * Props for ImpactAnalysis component
 */
interface ImpactAnalysisProps {
  /** The focal service being analyzed */
  service: Service;
  /** All dependencies in the system */
  allDependencies: GraphQLDependency[];
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if any */
  error?: string | null;
}

/**
 * Calculate transitive impact of a service outage
 */
function calculateImpact(
  serviceId: string,
  allDependencies: GraphQLDependency[],
  maxDepth: number = 5
): ImpactLevel[] {
  const impacts: Map<string, ImpactLevel> = new Map();
  const visited: Set<string> = new Set();

  function traverse(currentId: string, level: number, path: string[]): void {
    if (level > maxDepth || visited.has(currentId)) return;

    visited.add(currentId);

    // Find all services that depend on currentId
    const dependents = allDependencies.filter((dep) => dep.toServiceId === currentId);

    for (const dep of dependents) {
      if (!dep.fromService) continue;

      const depService = dep.fromService;
      const newPath = [...path, dep.fromServiceId];
      const newDepType = dep.metadata.dependencyType as 'hard' | 'soft';

      // If this is a new service or we found a shorter path, record it
      if (!impacts.has(dep.fromServiceId) || (impacts.get(dep.fromServiceId)?.level ?? 0) > level) {
        impacts.set(dep.fromServiceId, {
          service: depService,
          level,
          path: newPath,
          dependencyType: newDepType,
          criticality: dep.metadata.criticality,
        });
      }

      // Continue traversal if this is a hard dependency
      // (soft dependencies don't cascade failures)
      if (newDepType === 'hard') {
        traverse(dep.fromServiceId, level + 1, newPath);
      }
    }

    visited.delete(currentId);
  }

  traverse(serviceId, 0, [serviceId]);

  return Array.from(impacts.values()).sort((a, b) => {
    // Sort by level first, then by criticality
    if (a.level !== b.level) return a.level - b.level;
    const criticalityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const aCrit = criticalityOrder[a.criticality as keyof typeof criticalityOrder] ?? 4;
    const bCrit = criticalityOrder[b.criticality as keyof typeof criticalityOrder] ?? 4;
    return aCrit - bCrit;
  });
}

/**
 * Get severity color based on dependency type and criticality
 */
function getSeverityColor(dependencyType: string, criticality?: string | null): string {
  if (dependencyType === 'hard') {
    switch (criticality) {
      case 'critical':
        return 'var(--forge-theme-error, #D32F2F)';
      case 'high':
        return 'var(--forge-theme-warning, #F57C00)';
      default:
        return 'var(--forge-theme-primary, #2196F3)';
    }
  }
  return 'var(--forge-theme-text-medium, #999)';
}

/**
 * ImpactAnalysis Component
 */
export default function ImpactAnalysis({
  service,
  allDependencies,
  loading = false,
  error = null,
}: ImpactAnalysisProps): JSX.Element {
  // Calculate impact levels
  const impactLevels = useMemo(() => {
    if (!allDependencies.length) return [];
    return calculateImpact(service.entityId, allDependencies, 5);
  }, [service.entityId, allDependencies]);

  // Group by impact level
  const impactByLevel = useMemo(() => {
    const grouped = new Map<number, ImpactLevel[]>();
    for (const impact of impactLevels) {
      const levelImpacts = grouped.get(impact.level) ?? [];
      levelImpacts.push(impact);
      grouped.set(impact.level, levelImpacts);
    }
    return grouped;
  }, [impactLevels]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalImpacted = impactLevels.length;
    const directImpacted = impactLevels.filter((i) => i.level === 0).length;
    const hardDependencies = impactLevels.filter((i) => i.dependencyType === 'hard').length;
    const criticalImpacted = impactLevels.filter((i) => i.criticality === 'critical').length;

    return {
      totalImpacted,
      directImpacted,
      hardDependencies,
      criticalImpacted,
    };
  }, [impactLevels]);

  // Loading state
  if (loading) {
    return (
      <div className="impact-analysis">
        <LoadingSpinner message="Analyzing impact..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="impact-analysis">
        <EmptyState title="Failed to analyze impact" description={error} icon="error" />
      </div>
    );
  }

  // Empty state
  if (impactLevels.length === 0) {
    return (
      <div className="impact-analysis">
        <EmptyState
          title="No impact detected"
          description={`No services depend on ${service.name}. This service has no blast radius.`}
          icon="info"
        />
      </div>
    );
  }

  return (
    <div className="impact-analysis">
      {/* Header */}
      <div className="impact-header">
        <div className="impact-title-section">
          <h2>Impact Analysis</h2>
          <p className="impact-subtitle">
            Services that would be affected if <strong>{service.name}</strong> becomes unavailable
          </p>
        </div>
      </div>

      {/* Statistics */}
      <div className="impact-stats">
        <div className="impact-stat-card">
          <forge-icon name="warning"></forge-icon>
          <div className="impact-stat-content">
            <div className="impact-stat-value">{stats.totalImpacted}</div>
            <div className="impact-stat-label">Total Services Impacted</div>
          </div>
        </div>
        <div className="impact-stat-card">
          <forge-icon name="link"></forge-icon>
          <div className="impact-stat-content">
            <div className="impact-stat-value">{stats.directImpacted}</div>
            <div className="impact-stat-label">Direct Dependents</div>
          </div>
        </div>
        <div className="impact-stat-card">
          <forge-icon name="priority_high"></forge-icon>
          <div className="impact-stat-content">
            <div className="impact-stat-value">{stats.hardDependencies}</div>
            <div className="impact-stat-label">Hard Dependencies</div>
          </div>
        </div>
        <div className="impact-stat-card impact-stat-critical">
          <forge-icon name="error"></forge-icon>
          <div className="impact-stat-content">
            <div className="impact-stat-value">{stats.criticalImpacted}</div>
            <div className="impact-stat-label">Critical Services</div>
          </div>
        </div>
      </div>

      {/* Impact Levels */}
      <div className="impact-levels">
        {Array.from(impactByLevel.entries()).map(([level, impacts]) => (
          <div key={level} className="impact-level-section">
            <h3 className="impact-level-title">
              {level === 0 ? 'Direct Impact' : `Level ${level} Impact`}
              <span className="impact-level-badge">{impacts.length} services</span>
            </h3>
            <div className="impact-level-description">
              {level === 0
                ? 'Services that directly depend on this service'
                : `Services affected through ${level}-level transitive dependencies`}
            </div>

            <div className="impact-items">
              {impacts.map((impact) => (
                <div key={impact.service.entityId} className="impact-item">
                  <div
                    className="impact-item-indicator"
                    style={{
                      backgroundColor: getSeverityColor(impact.dependencyType, impact.criticality),
                    }}
                  />
                  <div className="impact-item-content">
                    <div className="impact-item-header">
                      <Link
                        to="/services/$serviceId"
                        params={{ serviceId: impact.service.entityId }}
                        className="impact-item-name"
                      >
                        {impact.service.name}
                      </Link>
                      <div className="impact-item-badges">
                        {impact.dependencyType === 'hard' && (
                          <span className="impact-badge impact-badge-hard">Hard</span>
                        )}
                        {impact.criticality && (
                          <span className={`impact-badge impact-badge-${impact.criticality}`}>
                            {impact.criticality}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="impact-item-type">
                      {impact.service.serviceType} • {impact.service.tier}
                    </div>
                    {impact.level > 0 && (
                      <div className="impact-item-path">
                        <forge-icon name="trending_flat"></forge-icon>
                        <span>Dependency chain depth: {impact.level + 1}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="impact-legend">
        <div className="impact-legend-title">Legend</div>
        <div className="impact-legend-items">
          <div className="impact-legend-item">
            <div className="impact-legend-indicator impact-legend-indicator--critical" />
            <span>Hard + Critical</span>
          </div>
          <div className="impact-legend-item">
            <div className="impact-legend-indicator impact-legend-indicator--high" />
            <span>Hard + High</span>
          </div>
          <div className="impact-legend-item">
            <div className="impact-legend-indicator impact-legend-indicator--hard" />
            <span>Hard Dependency</span>
          </div>
          <div className="impact-legend-item">
            <div className="impact-legend-indicator impact-legend-indicator--soft" />
            <span>Soft Dependency</span>
          </div>
        </div>
      </div>
    </div>
  );
}
